import { audio } from '../audio/audio';
import { dist } from '../core/util';
import { COMMANDER_BY_ID } from '../data/commanders';
import { FAMILY_BY_ID } from '../data/families';
import { LAYOUT } from '../data/ruleset';
import { SHOP, SHOP_BY_ID } from '../data/shop';
import type { Difficulty, Lane, ShopBranchId, Side } from '../data/types';
import type { NetCommand, NetLink } from '../net/types';
import { Effects } from '../render/effects';
import {
  HUD_RECTS,
  deckChips,
  LANE_COLORS,
  LANE_NAMES,
  Renderer,
  TUTORIAL_RECT,
  TUTORIAL_SKIP_RECT,
  type DragState,
  type HudState,
  type Coach,
  type Rect,
} from '../render/renderer';
import { AiController } from '../sim/ai';
import { Match, tileCenter, tileRect, type MatchConfig, type SimEvent } from '../sim/match';
import { MatchTelemetry, storeMatch, uploadMatch, type MatchRecord } from '../telemetry/telemetry';

const FIXED_DT = 1 / 60;
const MAX_STEPS = 6;
/** Online commands land this many ticks in the future so both clients apply them together. */
const INPUT_DELAY = 30;
const ONLINE_MAX_STEPS = 30;

export interface GameCallbacks {
  onEnd: (record: MatchRecord) => void;
  onPause: () => void;
}

interface TutorialStep {
  id: 'summon' | 'lane' | 'merge' | 'bond' | 'shop' | 'boss';
  text: string;
  highlight: HudState['highlight'];
  /** Returns true when the step is complete. */
  done: (e: string, data: Record<string, unknown>) => boolean;
  /** Optional auto-advance after N seconds. */
  timeout?: number;
}

const TUTORIAL: TutorialStep[] = [
  {
    id: 'summon',
    text: 'Tap Summon. A tower appears and starts sending minions down its lane.',
    highlight: 'summon',
    done: (e) => e === 'summon',
  },
  {
    id: 'lane',
    text: 'Tap your tower to send its minions left or right. The tower stays on its cell.',
    highlight: 'tiles',
    done: (e) => e === 'lane_switch',
  },
  {
    id: 'merge',
    text: 'Drag one tower onto an identical one.',
    highlight: 'summon',
    done: (e) => e === 'merge',
  },
  {
    id: 'bond',
    text: 'Towers on neighbouring cells share a bond.',
    highlight: null,
    done: () => false,
  },
  {
    id: 'shop',
    text: 'The shop boosts your whole board. Buy an upgrade with spare Essence.',
    highlight: 'shop',
    done: (e) => e === 'shop_buy',
  },
  {
    id: 'boss',
    text: 'Your bosses are already buffing this match. At 1:30 the field clears and one boss marches a random lane.',
    highlight: null,
    done: () => false,
    timeout: 8,
  },
];

export class Game {
  readonly match: Match;
  private readonly ai: AiController | null;
  private readonly link: NetLink | null;
  readonly localSide: Side;
  private readonly fx = new Effects();
  private readonly renderer: Renderer;
  private readonly telemetry: MatchTelemetry;
  private pending: NetCommand[] = [];
  private seq = 0;
  private netTick = 0;
  private readonly hud: HudState = {
    toast: null,
    banner: null,
    info: null,
    pressed: null,
    highlight: null,
    lowEssenceFlash: 0,
    tutorial: null,
    coach: null,
    inspect: null,
  };
  private drag: DragState | null = null;
  private deckHold: { kind: 'unit' | 'boss'; index: number; x: number; y: number; left: number } | null = null;
  private accumulator = 0;
  private lastFrame = 0;
  private raf = 0;
  paused = false;
  private finished = false;
  private endTimer = -1;
  private tutorialStep = -1;
  private tutorialTimer = 0;
  private sawBond = false;
  private pointerId: number | null = null;
  private toLogical: (clientX: number, clientY: number) => { x: number; y: number };
  private bossAlive = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    config: MatchConfig,
    difficulty: Difficulty,
    private readonly callbacks: GameCallbacks,
    toLogical: (clientX: number, clientY: number) => { x: number; y: number },
    tutorial: boolean,
    link: NetLink | null = null,
  ) {
    this.match = new Match(config);
    this.link = link;
    this.localSide = link?.localSide ?? 0;
    this.ai = link ? null : new AiController(this.match, 1, difficulty);
    this.renderer = new Renderer(ctx);
    this.renderer.viewSide = this.localSide;
    this.telemetry = new MatchTelemetry(this.match, difficulty, this.localSide, link ? 'online' : 'bot');
    this.toLogical = toLogical;
    if (tutorial && this.ai) {
      this.setTutorialStep(0);
      this.ai.calmUntil = 70;
    } else {
      this.banner('Hold both lanes', 'Every 90 seconds the field clears and one boss marches.', '#ffd23a', 3.2);
    }
    this.bindInput();
  }

  // ------------------------------------------------------------------ loop

  start(): void {
    this.lastFrame = performance.now();
    const frame = (now: number): void => {
      this.raf = requestAnimationFrame(frame);
      const dt = Math.min(0.1, (now - this.lastFrame) / 1000);
      this.lastFrame = now;
      this.update(dt);
      this.render(dt);
    };
    this.raf = requestAnimationFrame(frame);
  }

  private stopped = false;

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    cancelAnimationFrame(this.raf);
    this.unbindInput();
    this.link?.close();
  }

  get live(): boolean {
    return this.link !== null;
  }

  setPaused(p: boolean): void {
    this.paused = p;
    if (p) this.telemetry.ui('pause');
    else this.telemetry.ui('resume');
  }

  /** During the first tutorial step the world waits for the player's first summon. */
  private get worldFrozen(): boolean {
    return this.tutorialStep === 0;
  }

  private stepLocal(dt: number): void {
    this.accumulator += dt;
    let steps = 0;
    while (this.accumulator >= FIXED_DT && steps < MAX_STEPS) {
      this.match.step(FIXED_DT);
      this.ai?.update(FIXED_DT);
      this.accumulator -= FIXED_DT;
      steps++;
    }
    if (steps === MAX_STEPS) this.accumulator = 0;
  }

  /** Both clients step from the shared start time and apply commands that are due. */
  private stepOnline(): void {
    const link = this.link;
    if (!link || this.match.ended) return;
    for (const cmd of link.take()) this.queue(cmd);
    const target = Math.max(0, Math.floor((Date.now() - link.startAt) / (FIXED_DT * 1000)));
    let steps = 0;
    while (this.netTick < target && steps < ONLINE_MAX_STEPS && !this.match.ended) {
      this.flush(this.netTick);
      this.match.step(FIXED_DT);
      this.netTick++;
      steps++;
    }
  }

  private queue(cmd: NetCommand): void {
    if (cmd.n < 0) return;
    if (this.pending.some((c) => c.side === cmd.side && c.n === cmd.n)) return;
    this.pending.push(cmd);
  }

  private flush(tick: number): void {
    let due: NetCommand[] | null = null;
    const kept: NetCommand[] = [];
    for (const c of this.pending) {
      if (c.tick <= tick) (due ??= []).push(c);
      else kept.push(c);
    }
    if (!due) return;
    this.pending = kept;
    due.sort((a, b) => a.tick - b.tick || a.side - b.side || a.n - b.n);
    for (const cmd of due) this.apply(cmd);
  }

  private apply(cmd: NetCommand): void {
    switch (cmd.op) {
      case 'summon': {
        const teachBond =
          this.tutorialStep >= 0 &&
          TUTORIAL[this.tutorialStep].id === 'bond' &&
          cmd.side === this.localSide &&
          !this.match.elementLinks.some((l) => l.side === cmd.side);
        this.match.summon(cmd.side, teachBond);
        break;
      }
      case 'lane':
        if (cmd.tile !== undefined) this.match.toggleLane(cmd.side, cmd.tile);
        break;
      case 'drop':
        if (cmd.from !== undefined && cmd.to !== undefined) this.match.drop(cmd.side, cmd.from, cmd.to);
        break;
      case 'buy':
        if (cmd.branch) this.match.buyUpgrade(cmd.side, cmd.branch as ShopBranchId);
        break;
      case 'commander':
        this.match.useCommander(cmd.side);
        break;
      default:
        break;
    }
  }

  private issue(partial: Omit<NetCommand, 'tick' | 'side' | 'n'>): void {
    if (!this.link) {
      this.apply({ ...partial, tick: 0, side: this.localSide, n: this.seq++ });
      return;
    }
    const cmd: NetCommand = { ...partial, tick: this.netTick + INPUT_DELAY, side: this.localSide, n: this.seq++ };
    this.queue(cmd);
    this.link.send(cmd);
  }

  private reasonForYou(reason: string): string {
    if (this.localSide === 0) return reason;
    if (reason === 'Your fortress fell') return 'Enemy fortress destroyed';
    if (reason === 'Enemy fortress destroyed') return 'Your fortress fell';
    return reason;
  }

  private update(dt: number): void {
    const hold = this.finished || this.worldFrozen || (this.paused && !this.link);
    if (!hold) {
      if (this.link) this.stepOnline();
      else this.stepLocal(dt);
    }
    // Commands issued while frozen (e.g. the tutorial's first summon) still produce events.
    this.handleEvents(this.match.drainEvents());
    this.fx.update(dt);
    this.tickHud(dt);
    if (this.endTimer >= 0) {
      this.endTimer -= dt;
      if (this.endTimer < 0) this.finish();
    }
    // Music intensity follows the match.
    const pressure = this.match.lanePressure(this.localSide);
    const p = Math.min(1, (pressure[0] + pressure[1]) / 900);
    audio.intensity = this.match.overtime ? 1 : Math.max(this.bossAlive > 0 ? 0.7 : 0.15, p);
  }

  private render(dt: number): void {
    this.renderer.noteSpawnIntervals(this.match);
    this.renderer.render(this.match, this.fx, this.drag, this.hud, dt);
  }

  private tickHud(dt: number): void {
    if (this.hud.toast) {
      this.hud.toast.life -= dt;
      if (this.hud.toast.life <= 0) this.hud.toast = null;
    }
    if (this.hud.banner) {
      this.hud.banner.life -= dt;
      if (this.hud.banner.life <= 0) this.hud.banner = null;
    }
    if (this.hud.info) {
      this.hud.info.life -= dt;
      if (this.hud.info.life <= 0 || !this.match.players[this.localSide].towers[this.hud.info.tile]) this.hud.info = null;
    }
    if (this.hud.lowEssenceFlash > 0) this.hud.lowEssenceFlash -= dt;
    if (this.deckHold) {
      this.deckHold.left -= dt;
      if (this.deckHold.left <= 0) this.hud.inspect = { kind: this.deckHold.kind, index: this.deckHold.index };
    }
    if (this.tutorialStep >= 0) {
      const step = TUTORIAL[this.tutorialStep];
      if (!this.paused) this.tutorialTimer += dt;
      if (step.timeout !== undefined && this.tutorialTimer >= step.timeout) {
        this.setTutorialStep(this.tutorialStep + 1);
        return;
      }
      this.refreshCoach();
    } else this.hud.coach = null;
    let bosses = 0;
    for (const m of this.match.minions) if (m.isBoss) bosses++;
    this.bossAlive = bosses;
  }

  // -------------------------------------------------------------- tutorial

  private setTutorialStep(i: number): void {
    if (i >= TUTORIAL.length) {
      this.tutorialStep = -1;
      this.hud.tutorial = null;
      this.hud.highlight = null;
      this.hud.coach = null;
      this.telemetry.ui('tutorial_complete');
      return;
    }
    this.tutorialStep = i;
    this.tutorialTimer = 0;
    this.sawBond = false;
    const step = TUTORIAL[i];
    this.hud.tutorial = { text: step.text, step: i + 1, total: TUTORIAL.length };
    this.hud.highlight = step.highlight;
    this.telemetry.ui('tutorial_step', { step: i + 1 });
  }

  private skipTutorial(): void {
    this.telemetry.ui('tutorial_skip', { step: this.tutorialStep + 1 });
    this.tutorialStep = -1;
    this.hud.tutorial = null;
    this.hud.highlight = null;
    this.hud.coach = null;
  }

  /** Points the lesson at the control that matters for the current board. */
  private refreshCoach(): void {
    const step = TUTORIAL[this.tutorialStep];
    if (!step) return;
    const side = this.localSide;
    let text = step.text;
    let highlight = step.highlight;
    const coach: Coach = { arrow: null, drag: null, tiles: [] };
    if (step.id === 'summon') {
      coach.arrow = { kind: 'summon' };
    } else if (step.id === 'lane') {
      const tower = this.match.towersOf(side)[0];
      if (tower) coach.arrow = { kind: 'tile', tile: tower.tile };
    } else if (step.id === 'merge') {
      const pair = this.match.mergePairs(side)[0];
      if (!pair) {
        text = 'You need two identical towers. Tap Summon until a match appears.';
        highlight = 'summon';
        coach.arrow = { kind: 'summon' };
      } else {
        text = 'Drag one glowing tower onto the other. The merge raises its rank, and its minions get stronger too.';
        highlight = null;
        coach.drag = { from: pair[0], to: pair[1] };
        coach.tiles = [pair[0], pair[1]];
      }
    } else if (step.id === 'bond') {
      const link = this.match.elementLinks.find((l) => l.side === side);
      if (link) {
        if (!this.sawBond) {
          this.sawBond = true;
          this.tutorialTimer = 0;
        }
        text = 'That shared edge is a bond. Bright gold is the best match, a dim edge is the same element, and black is a clash.';
        highlight = null;
        coach.arrow = { kind: 'bond', a: link.tileA, b: link.tileB };
        coach.tiles = [link.tileA, link.tileB];
        if (this.tutorialTimer > 6.5) {
          this.setTutorialStep(this.tutorialStep + 1);
          return;
        }
      } else if (this.tutorialTimer > 18) {
        this.setTutorialStep(this.tutorialStep + 1);
        return;
      } else {
        text = 'Summon beside a tower, not diagonally. A bond mark appears on the edge they share.';
        highlight = 'summon';
        coach.arrow = { kind: 'summon' };
      }
    } else if (step.id === 'shop') {
      coach.arrow = { kind: 'shop' };
    } else if (step.id === 'boss') {
      coach.arrow = { kind: 'bosses' };
    }
    this.hud.tutorial = { text, step: this.tutorialStep + 1, total: TUTORIAL.length };
    this.hud.highlight = highlight;
    this.hud.coach = coach;
  }

  private tutorialEvent(type: string, data: Record<string, unknown> = {}): void {
    if (this.tutorialStep < 0) return;
    const step = TUTORIAL[this.tutorialStep];
    if (step.done(type, data)) this.setTutorialStep(this.tutorialStep + 1);
  }

  get tutorialActive(): boolean {
    return this.tutorialStep >= 0;
  }

  // ---------------------------------------------------------------- events

  private toast(text: string, color = '#ffffff', life = 2.2): void {
    this.hud.toast = { text, life, maxLife: life, color };
  }

  private banner(title: string, subtitle: string, color: string, life = 4): void {
    this.hud.banner = { title, subtitle, life, maxLife: life, color };
  }

  private handleEvents(events: SimEvent[]): void {
    for (const e of events) {
      switch (e.type) {
        case 'summon': {
          const fam = FAMILY_BY_ID[e.familyId];
          this.fx.burst(e.x, e.y, fam.color, 18, 140, 0.5, 4, 'square');
          this.fx.ring(e.x, e.y, fam.accent, 40, 0.4);
          if (e.side === this.localSide) {
            audio.play('summon');
            this.tutorialEvent('summon');
          }
          break;
        }
        case 'merge': {
          const fam = FAMILY_BY_ID[e.familyId];
          this.fx.burst(e.x, e.y, fam.accent, 30, 220, 0.6, 4, 'spark');
          this.fx.ring(e.x, e.y, '#ffffff', 50, 0.45);
          this.fx.text(e.x, e.y - 30, `${fam.name.toUpperCase()} ${e.rank}`, e.rank >= 5 ? '#ffd23a' : '#ffffff', 14);
          if (e.side === this.localSide) {
            audio.play('merge');
            this.fx.addShake(3);
            this.tutorialEvent('merge');
          }
          break;
        }
        case 'lane': {
          this.fx.ring(e.x, e.y, LANE_COLORS[e.lane], 36, 0.35);
          if (e.side === this.localSide) {
            this.fx.text(e.x, e.y - 34, e.lane === 0 ? '◄ LEFT' : 'RIGHT ►', LANE_COLORS[e.lane], 13);
            audio.play('lane');
            this.tutorialEvent('lane_switch');
          }
          break;
        }
        case 'shot': {
          if (e.kind === 'mortar') audio.play('mortar', 0.6);
          else if (e.kind === 'fireball') audio.play('fireball', 0.6);
          else if (e.kind === 'fire') audio.play('fireball', 0.4);
          else audio.play('shot', 0.5);
          this.fx.burst(e.x, e.y, '#ffffff', 2, 60, 0.15, 2, 'spark');
          break;
        }
        case 'hit': {
          if (e.kind === 'melee') this.fx.burst(e.x, e.y - 6, '#ffffff', 3, 70, 0.2, 2, 'spark');
          else if (e.kind === 'laser') {
            this.fx.burst(e.x, e.y - 6, '#d3ffb8', 2, 50, 0.2, 2);
            audio.play('laser', 0.4);
          } else this.fx.burst(e.x, e.y - 6, '#ffe9b0', 3, 90, 0.2, 2, 'spark');
          if (e.side !== this.localSide && e.damage > 0) {
            this.fx.text(e.x, e.y - 16, String(Math.max(1, Math.round(e.damage))), '#fff4d6', 10, true, 0.55);
          }
          audio.play('hit', 0.35);
          break;
        }
        case 'death': {
          const fam = Object.values(FAMILY_BY_ID).find((f) => f.faction === e.faction);
          const col = fam ? fam.color : '#ffffff';
          if (e.isBoss) {
            this.fx.burst(e.x, e.y, col, 60, 260, 1.0, 6, 'square', 200);
            this.fx.burst(e.x, e.y, '#ffd23a', 30, 180, 0.8, 4, 'spark');
            this.fx.ring(e.x, e.y, '#ffd23a', 90, 0.7);
            this.fx.addShake(8);
            audio.play('explosion');
            if (e.side !== this.localSide) this.toast('BOSS DESTROYED  +60 ESSENCE', '#ffd23a', 2.5);
          } else {
            this.fx.burst(e.x, e.y, col, 10 * e.scale, 130, 0.5, 3, 'square', 160);
            this.fx.burst(e.x, e.y, 'rgba(60,60,60,0.8)', 6, 40, 0.7, 5);
            audio.play('death', 0.6);
          }
          break;
        }
        case 'leak': {
          if (e.side === this.localSide) {
            this.fx.addShake(10);
            this.fx.addFlash('#ff2a4a', 0.35);
            this.fx.text(e.x, e.y - 20, `-${e.amount} ♥`, '#ff5c7a', 22);
            audio.play('leak');
            this.toast(`${LANE_NAMES[e.lane]} LANE LEAKED · ${this.match.players[this.localSide].hearts} HEARTS LEFT`, '#ff5c7a', 2);
          } else {
            this.fx.addFlash('#4ad9c8', 0.15);
            this.fx.text(e.x, e.y + 20, `-${e.amount} ♥`, '#4ad9c8', 22);
            this.fx.burst(e.x, e.y, '#4ad9c8', 20, 200, 0.6, 3, 'spark');
            audio.play('leakEnemy');
          }
          break;
        }
        case 'muster': {
          this.fx.tracer(e.x, e.y, e.tx, e.ty, LANE_COLORS[e.lane], 0.35);
          this.fx.ring(e.tx, e.ty, LANE_COLORS[e.lane], 14, 0.3);
          break;
        }
        case 'bossWarning': {
          if (e.side !== this.localSide) break;
          const lanes = e.lanes.map((l) => LANE_NAMES[l]).join(' & ');
          this.banner(e.name.toUpperCase(), `Marching down the ${lanes} lane${e.lanes.length > 1 ? 's' : ''} · ${e.description}`, '#ffd23a', 4.5);
          audio.play('boss');
          break;
        }
        case 'bossSpawn': {
          this.fx.ring(e.x, e.y, '#ffd23a', 60, 0.6);
          this.fx.addShake(4);
          break;
        }
        case 'pop': {
          this.fx.burst(e.x, e.y, '#ffd23a', 8 + e.rank * 3, 120, 0.35, 3, 'spark');
          this.fx.ring(e.x, e.y, '#ffffff', 16 + e.rank * 4, 0.25);
          break;
        }
        case 'heal': {
          if (e.amount >= 4) {
            this.fx.text(e.x, e.y - 18, `+${Math.round(e.amount)}`, '#8dff9a', 11, false, 0.7);
            this.fx.burst(e.x, e.y, '#8dff9a', 3, 40, 0.4, 2);
            audio.play('heal', 0.5);
          }
          break;
        }
        case 'explosion': {
          const col = e.kind === 'fire' ? '#ff7a2a' : e.kind === 'frost' ? '#bfe5ff' : e.kind === 'mortar' ? '#ffd23a' : '#ffb347';
          this.fx.burst(e.x, e.y, col, 16, 160, 0.45, 4);
          this.fx.burst(e.x, e.y, '#ffffff', 6, 120, 0.25, 2, 'spark');
          this.fx.ring(e.x, e.y, col, e.radius, 0.35);
          audio.play('explosion', 0.6);
          break;
        }
        case 'commander': {
          const cmd = COMMANDER_BY_ID[e.id];
          if (e.side === this.localSide) {
            this.banner(`${e.name.toUpperCase()}!`, cmd.activeDescription, cmd.color, 2.2);
            audio.play('commander');
            this.fx.addFlash(cmd.color, 0.2);
          } else {
            this.toast(`ENEMY USED ${e.name.toUpperCase()}`, cmd.color, 2);
          }
          break;
        }
        case 'shop': {
          if (e.side === this.localSide) {
            const s = SHOP_BY_ID[e.branch];
            this.toast(`${s.name.toUpperCase()} LV ${e.level} · ${s.description}`, s.color, 2.6);
            audio.play('shop');
            this.tutorialEvent('shop_buy');
          }
          break;
        }
        case 'overtime': {
          this.banner('SUDDEN DEATH', 'Leaks deal double damage · kills pay more', '#ff5c5c', 4);
          audio.play('overtime');
          break;
        }
        case 'essence': {
          if (e.side === this.localSide) this.fx.text(e.x + 14, e.y - 10, `+${e.amount}`, '#ffd23a', 11, true, 0.8);
          break;
        }
        case 'shove': {
          this.fx.ring(e.x, e.y, '#ffe6bd', 40, 0.4);
          this.fx.addShake(2);
          break;
        }
        case 'ignore': {
          this.fx.text(e.x, e.y - 16, 'BLOCK', '#cfcfcf', 9, true, 0.5);
          break;
        }
        case 'error': {
          if (e.side === this.localSide) {
            this.toast(e.message.toUpperCase(), '#ff8a8a', 1.4);
            this.hud.lowEssenceFlash = 0.6;
            audio.play('error');
          }
          break;
        }
        case 'end': {
          this.finished = true;
          this.endTimer = 1.4;
          const reason = this.reasonForYou(e.reason);
          if (e.winner === this.localSide) {
            this.banner('VICTORY', reason, '#4ad9c8', 3);
            audio.play('win');
            this.fx.burst(LAYOUT.width / 2, LAYOUT.height / 2, '#4ad9c8', 80, 320, 1.4, 5, 'square', 220);
          } else if (e.winner === (1 - this.localSide)) {
            this.banner('DEFEAT', reason, '#ff5c5c', 3);
            audio.play('lose');
            this.fx.addFlash('#ff2a4a', 0.5);
          } else {
            this.banner('DRAW', reason, '#ffffff', 3);
          }
          break;
        }
      }
    }
  }

  private finish(): void {
    this.endTimer = -1;
    const record = this.telemetry.finish();
    storeMatch(record);
    void uploadMatch(record);
    this.callbacks.onEnd(record);
  }

  // ----------------------------------------------------------------- input

  private onDown = (ev: PointerEvent): void => {
    if (this.pointerId !== null) return;
    this.pointerId = ev.pointerId;
    try {
      this.canvas.setPointerCapture(ev.pointerId);
    } catch {
      /* synthetic or already-released pointer */
    }
    const { x, y } = this.toLogical(ev.clientX, ev.clientY);
    this.pointerDown(x, y);
  };

  private onMove = (ev: PointerEvent): void => {
    if (ev.pointerId !== this.pointerId) return;
    const { x, y } = this.toLogical(ev.clientX, ev.clientY);
    this.pointerMove(x, y);
  };

  private onUp = (ev: PointerEvent): void => {
    if (ev.pointerId !== this.pointerId) return;
    this.pointerId = null;
    const { x, y } = this.toLogical(ev.clientX, ev.clientY);
    this.pointerUp(x, y);
  };

  private onCancel = (ev: PointerEvent): void => {
    if (ev.pointerId !== this.pointerId) return;
    this.pointerId = null;
    this.deckHold = null;
    this.hud.inspect = null;
    const drag = this.drag;
    this.drag = null;
    this.hud.pressed = null;
    if (drag?.started && drag.hoverTile >= 0) this.commitDrop(drag.tile, drag.hoverTile);
    else if (drag?.started) audio.play('drop');
  };

  private commitDrop(from: number, target: number): void {
    if (target < 0 || target === from) {
      audio.play('drop');
      return;
    }
    const blocked = this.match.whyNotMerge(this.localSide, from, target);
    const before = this.match.players[this.localSide].stats.merges;
    this.issue({ op: 'drop', from, to: target });
    const merged = !this.link && this.match.players[this.localSide].stats.merges > before;
    if (!merged) audio.play('drop');
    if (!merged && blocked) this.toast(blocked, '#ffb0b0', 1.3);
  }

  private bindInput(): void {
    this.canvas.addEventListener('pointerdown', this.onDown);
    this.canvas.addEventListener('pointermove', this.onMove);
    this.canvas.addEventListener('pointerup', this.onUp);
    this.canvas.addEventListener('pointercancel', this.onCancel);
  }

  private unbindInput(): void {
    this.canvas.removeEventListener('pointerdown', this.onDown);
    this.canvas.removeEventListener('pointermove', this.onMove);
    this.canvas.removeEventListener('pointerup', this.onUp);
    this.canvas.removeEventListener('pointercancel', this.onCancel);
  }

  private inRect(x: number, y: number, r: Rect): boolean {
    return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
  }

  private tileAt(x: number, y: number): number {
    const hit = this.nearestTiles(x, y, 54);
    return hit.length ? hit[0].tile : -1;
  }

  /** Tiles whose center is within `max` px, nearest first. The seam between cells goes to the closer one. */
  private nearestTiles(x: number, y: number, max: number): Array<{ tile: number; dist: number }> {
    const hits: Array<{ tile: number; dist: number }> = [];
    for (let i = 0; i < 15; i++) {
      const c = tileCenter(this.localSide, i);
      const d = Math.hypot(c.x - x, c.y - y);
      if (d <= max) hits.push({ tile: i, dist: d });
    }
    hits.sort((a, b) => a.dist - b.dist);
    return hits;
  }

  /**
   * Where a drag should land. Aims at the floating picture, not the fingertip,
   * and if the fingertip is on the seam, prefers the tower that can actually merge.
   */
  private dropTile(x: number, y: number, from: number): number {
    const lift = this.localSide === 0 ? -12 : 12;
    const hits = this.nearestTiles(x, y + lift, 58);
    if (!hits.length) return this.tileAt(x, y);
    const merge = (tile: number): boolean => this.match.canMerge(this.localSide, from, tile);
    const best = hits[0];
    const partner = hits.find((h) => merge(h.tile));
    if (partner && !merge(best.tile) && partner.dist - best.dist < 18) return partner.tile;
    return best.tile;
  }

  /** Screen position to simulation position. Side 1 sees the board flipped. */
  private toSim(x: number, y: number): { x: number; y: number } {
    if (this.localSide === 0) return { x, y };
    return { x, y: LAYOUT.height - y };
  }

  private pointerDown(x: number, y: number): void {
    audio.init();
    if (this.finished) return;
    if (this.inRect(x, y, HUD_RECTS.pause)) {
      audio.play('click');
      this.callbacks.onPause();
      return;
    }
    if (this.paused) return;

    const chip = deckChips().find((c) => this.inRect(x, y, c.rect));
    if (chip) {
      this.deckHold = { kind: chip.kind, index: chip.index, x, y, left: 0.32 };
      this.hud.inspect = null;
      return;
    }

    if (this.hud.tutorial && this.inRect(x, y, TUTORIAL_SKIP_RECT)) {
      audio.play('click');
      this.skipTutorial();
      return;
    }
    if (this.hud.tutorial && this.inRect(x, y, TUTORIAL_RECT)) return;

    if (this.inRect(x, y, HUD_RECTS.summon)) {
      this.hud.pressed = 'summon';
      this.issue({ op: 'summon' });
      return;
    }
    if (this.inRect(x, y, HUD_RECTS.commander)) {
      this.hud.pressed = 'commander';
      const p = this.match.players[this.localSide];
      if (p.commanderCooldown > 0) {
        this.toast(`${COMMANDER_BY_ID[p.commanderId].activeName.toUpperCase()} READY IN ${Math.ceil(p.commanderCooldown)}s`, '#ffffff', 1.2);
        audio.play('error', 0.5);
      } else {
        this.issue({ op: 'commander' });
      }
      return;
    }
    for (let i = 0; i < SHOP.length; i++) {
      if (this.inRect(x, y, HUD_RECTS.shop[i])) {
        const s = SHOP[i];
        this.hud.pressed = `shop:${s.id}`;
        const before = this.match.players[this.localSide].shop[s.id];
        this.issue({ op: 'buy', branch: s.id });
        if (this.match.players[this.localSide].shop[s.id] === before && !this.link) {
          const c = this.match.shopCost(this.localSide, s.id);
          if (c !== null) this.toast(`${s.name.toUpperCase()} · ${s.description}`, s.color, 2.2);
        }
        return;
      }
    }

    const sim = this.toSim(x, y);
    const tile = this.tileAt(sim.x, sim.y);
    if (tile >= 0) {
      const t = this.match.players[this.localSide].towers[tile];
      if (t) {
        this.drag = { tile, x: sim.x, y: sim.y, hoverTile: -1, started: false };
      } else {
        this.toast('EMPTY PLOT · TAP SUMMON TO PLACE A TOWER', 'rgba(255,255,255,0.8)', 1.4);
      }
      return;
    }

    const enemy = (1 - this.localSide) as Side;
    for (let i = 0; i < 15; i++) {
      const r = tileRect(enemy, i);
      if (this.inRect(sim.x, sim.y, r)) {
        const t = this.match.players[enemy].towers[i];
        if (t) {
          const fam = FAMILY_BY_ID[t.familyId];
          this.toast(`ENEMY ${fam.name.toUpperCase()} RANK ${t.rank} · ${LANE_NAMES[t.lane]} LANE`, fam.color, 1.8);
        }
        return;
      }
    }
  }

  private pointerMove(x: number, y: number): void {
    if (this.deckHold && dist(x, y, this.deckHold.x, this.deckHold.y) > 14) {
      this.deckHold = null;
      this.hud.inspect = null;
    }
    if (!this.drag) return;
    const sim = this.toSim(x, y);
    this.drag.x = sim.x;
    this.drag.y = sim.y;
    if (!this.drag.started && dist(sim.x, sim.y, ...this.dragOrigin()) > 12) {
      this.drag.started = true;
      this.hud.info = null;
      audio.play('pickup');
    }
    if (this.drag.started) this.drag.hoverTile = this.dropTile(sim.x, sim.y, this.drag.tile);
  }

  private dragOrigin(): [number, number] {
    const r = tileRect(this.localSide, this.drag!.tile);
    return [r.x + r.w / 2, r.y + r.h / 2];
  }

  private pointerUp(x: number, y: number): void {
    this.hud.pressed = null;
    if (this.deckHold) {
      this.deckHold = null;
      this.hud.inspect = null;
    }
    if (!this.drag) return;
    const d = this.drag;
    this.drag = null;
    if (this.finished || this.paused) return;
    if (d.started) {
      const sim = this.toSim(x, y);
      const released = this.dropTile(sim.x, sim.y, d.tile);
      const target = released >= 0 ? released : d.hoverTile;
      this.commitDrop(d.tile, target);
      return;
    }
    // Tap: arrows flip the lane. Painted cells already chose it.
    if (this.match.board !== 'painted') this.issue({ op: 'lane', tile: d.tile });
    this.hud.info = { tile: d.tile, life: 3 };
  }

  /** Command surface for automation / debugging from the console. */
  get commands(): {
    summon: () => boolean;
    toggleLane: (tile: number) => boolean;
    setLane: (tile: number, lane: Lane) => boolean;
    drop: (from: number, to: number) => string;
    buy: (branch: string) => boolean;
    commander: () => boolean;
  } {
    return {
      summon: () => {
        const before = this.match.players[this.localSide].stats.summons;
        this.issue({ op: 'summon' });
        return this.match.players[this.localSide].stats.summons > before;
      },
      toggleLane: (tile) => {
        this.issue({ op: 'lane', tile });
        return true;
      },
      setLane: (tile, lane) => this.match.setLane(this.localSide, tile, lane),
      drop: (from, to) => {
        this.issue({ op: 'drop', from, to });
        return 'move';
      },
      buy: (branch) => {
        const before = this.match.players[this.localSide].shop[branch as ShopBranchId] ?? 0;
        this.issue({ op: 'buy', branch });
        return (this.match.players[this.localSide].shop[branch as ShopBranchId] ?? 0) > before;
      },
      commander: () => {
        const before = this.match.players[this.localSide].stats.commanderUses;
        this.issue({ op: 'commander' });
        return this.match.players[this.localSide].stats.commanderUses > before;
      },
    };
  }
}
